import { useState, useMemo, useEffect } from "react";
import { MaterialReactTable, useMaterialReactTable } from "material-react-table";
import {
  Visibility,
  PersonAdd,
  Group,
  ManageAccounts,
  Badge as BadgeIcon,
  FilterList,
  WarningAmber
} from "@mui/icons-material";
import {
  Box,
  Button,
  FormControl,
  MenuItem,
  Modal,
  Select,
  Switch,
  Typography,
  Chip,
  IconButton,
  Tooltip,
  Paper,
  Avatar,
  Divider,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress
} from "@mui/material";
import UsersUpdate from "./users-update";
import UsersDetail from "./users-detail";
import { useMutation, useQuery } from "react-query";
import useAxiosPrivate from "../../hooks/useAxiosPrivate";
import moment from "moment";
import { useSnackbar } from "notistack";
import { useForm } from "react-hook-form";
import Error from "../error";
import useUserPermissions from "../../hooks/useUserPermissions";
import {
  mrtManagementTableCustomActionsOuterSx,
  mrtManagementTablePrimaryActionButtonSx,
  mrtManagementTableToolbarOptions,
} from "../../utils/mrtManagementTableToolbar";

// Users list component powered by Material React Table
function Users() {
  const {
    createUser,
    getUser,
    updateUserStatus,
    updateUserRole,
    revokeUsersRole,
  } = useUserPermissions();

  // MRT State Management
  const [globalFilter, setGlobalFilter] = useState("");
  const [columnFilters, setColumnFilters] = useState([]);
  const [sorting, setSorting] = useState([{ id: "first_name", desc: false }]);
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 5 });

  // Dialog & View State
  const [open, setOpen] = useState(false);
  const [view, setView] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState(null);

  // Status Confirmation State
  const [statusDialog, setStatusDialog] = useState({ open: false, user: null });

  const { register, handleSubmit, watch } = useForm();
  const { enqueueSnackbar } = useSnackbar();
  const axiosPrivate = useAxiosPrivate();

  // Data Fetching Logic (Server-Side)
  const {
    isLoading: usersDataLoading,
    data: usersData,
    refetch,
    error: fetchUsersError,
  } = useQuery(
    ["users", pagination, globalFilter, columnFilters, sorting],
    () => {
      // Map MRT filters/sorting to our backend format
      const filterParams = globalFilter
        ? JSON.stringify([{
          column_field: "*",
          operator_value: "contains",
          value: JSON.stringify({
            columns: ["first_name", "middle_name", "last_name", "email", "phone"],
            value: globalFilter
          })
        }])
        : columnFilters.length > 0
          ? JSON.stringify(columnFilters.map(f => ({
            column_field: f.id,
            operator_value: "contains",
            value: f.value
          })))
          : "[]";

      const sortParams = JSON.stringify(sorting.map(s => ({
        field: s.id,
        sort: s.desc ? "desc" : "asc"
      })));

      return axiosPrivate.get(`/users`, {
        params: {
          page: pagination.pageIndex,
          per_page: pagination.pageSize,
          sort: sortParams,
          filter: filterParams,
        },
      });
    },
    { refetchOnWindowFocus: false }
  );

  const { data: rolesData } = useQuery(["roles"], () => axiosPrivate.get(`/roles`, { params: { page: -1 } }), { refetchOnWindowFocus: false });

  // Mutations
  const { mutate: toggleStatus, isLoading: isToggling } = useMutation((user) =>
    axiosPrivate.patch(`/users/${user.id}/status`, { status: user.status === "ACTIVE" ? "INACTIVE" : "ACTIVE" }),
    {
      onSuccess: () => {
        enqueueSnackbar("Status updated successfully", { variant: "success" });
        setStatusDialog({ open: false, user: null });
        refetch();
      }
    }
  );

  const { mutate: updateRole } = useMutation((roleData) =>
    roleData.role === ""
      ? axiosPrivate.delete(`/users/${selectedUserId}/role`)
      : axiosPrivate.patch(`/users/${selectedUserId}/role`, roleData),
    {
      onSuccess: () => {
        enqueueSnackbar("Role updated successfully", { variant: "success" });
        refetch();
      }
    }
  );

  // Handlers
  const handleView = (id) => {
    setSelectedUserId(id);
    setView(true);
    setOpen(true);
  };

  const handleOpenCreate = () => {
    setView(false);
    setOpen(true);
  };

  const handleModalClose = () => {
    setOpen(false);
    setSelectedUserId(null);
  };

  const handleStatusToggleRequest = (user) => {
    setStatusDialog({ open: true, user });
  };

  const handleConfirmStatusToggle = () => {
    if (statusDialog.user) {
      toggleStatus(statusDialog.user);
    }
  };

  // Columns Definition
  const columns = useMemo(() => [
    {
      accessorFn: (row) => `${row.first_name} ${row.last_name}`,
      id: "name",
      header: "User",
      size: 250,
      Cell: ({ row }) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.light', fontSize: '14px', fontWeight: 600 }}>
            {row.original.first_name?.[0]}{row.original.last_name?.[0]}
          </Avatar>
          <Box>
            <Typography variant="body2" sx={{ fontWeight: 700, color: 'secondary.main' }}>
              {row.original.first_name} {row.original.last_name}
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
              {row.original.email}
            </Typography>
          </Box>
        </Box>
      ),
    },
    {
      accessorKey: "phone",
      header: "Phone",
      size: 150,
    },
    {
      accessorKey: "role",
      header: "Access Level",
      size: 200,
      enableSorting: false,
      Cell: ({ row }) => (
        <FormControl size="small" fullWidth>
          <Select
            value={row.original.role || ""}
            disabled={row.original.role === "super-user" || !updateUserRole}
            onChange={(e) => {
              setSelectedUserId(row.original.id);
              updateRole({ role: e.target.value });
            }}
            sx={{
              borderRadius: 2.5,
              fontSize: '13px',
              bgcolor: 'action.hover',
              '& .MuiOutlinedInput-notchedOutline': { borderColor: 'divider' },
              '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'primary.main' }
            }}
          >
            <MenuItem value=""><em>None</em></MenuItem>
            {rolesData?.data?.data?.map((role) => (
              <MenuItem key={role.name} value={role.name}>{role.name}</MenuItem>
            ))}
          </Select>
        </FormControl>
      ),
    },
    {
      accessorKey: "status",
      header: "Status",
      size: 150,
      enableSorting: false,
      Cell: ({ cell, row }) => (
        <Stack direction="row" alignItems="center" spacing={1}>
          <Chip
            label={cell.getValue()}
            size="small"
            sx={{
              fontWeight: 800,
              fontSize: '11px',
              textTransform: 'uppercase',
              bgcolor: cell.getValue() === "ACTIVE" ? 'success.lighter' : 'error.lighter',
              color: cell.getValue() === "ACTIVE" ? 'success.main' : 'error.main',
              border: `1px solid`,
              borderColor: cell.getValue() === "ACTIVE" ? 'success.light' : 'error.light',
            }}
          />
          <Switch
            checked={cell.getValue() === "ACTIVE"}
            onChange={() => handleStatusToggleRequest(row.original)}
            size="small"
            disabled={!updateUserStatus}
          />
        </Stack>
      ),
    },
    {
      accessorKey: "created_at",
      header: "Joined",
      size: 150,
      Cell: ({ cell }) => moment(cell.getValue()).format("MMM D, YYYY"),
    },
  ], [rolesData, toggleStatus, updateRole, updateUserRole, updateUserStatus]);

  const table = useMaterialReactTable({
    columns,
    data: usersData?.data?.data || [],
    initialState: { showColumnFilters: false, density: 'comfortable' },
    manualFiltering: true,
    manualPagination: true,
    manualSorting: true,
    ...mrtManagementTableToolbarOptions,
    muiToolbarAlertBannerProps: fetchUsersError ? { color: 'error', children: 'Error loading data' } : undefined,
    onColumnFiltersChange: setColumnFilters,
    onGlobalFilterChange: setGlobalFilter,
    onPaginationChange: setPagination,
    onSortingChange: setSorting,
    rowCount: usersData?.data?.meta_data?.total || 0,
    state: { columnFilters, globalFilter, isLoading: usersDataLoading, pagination, sorting },
    enableRowActions: true,
    positionActionsColumn: 'last',
    renderRowActions: ({ row }) => (
      <Box sx={{ display: 'flex', gap: 1 }}>
        <Tooltip title="View Details">
          <IconButton size="small" onClick={() => handleView(row.original.id)} sx={{ color: 'secondary.main' }}>
            <Visibility fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
    ),
    renderTopToolbarCustomActions: () => (
      <Box sx={mrtManagementTableCustomActionsOuterSx}>
        <Stack direction="row" spacing={2} alignItems="center" sx={{ minWidth: 0 }}>
          <Box sx={{ p: 1, borderRadius: 2, bgcolor: 'primary.lighter', display: 'flex', flexShrink: 0 }}>
            <Group color="primary" />
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 800, color: 'secondary.main', lineHeight: 1.2 }}>
              Users Management
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500 }}>
              {usersData?.data?.meta_data?.total || 0} active identity profiles
            </Typography>
          </Box>
        </Stack>
        {createUser && (
          <Button
            variant="contained"
            startIcon={<PersonAdd />}
            onClick={handleOpenCreate}
            sx={{
              ...mrtManagementTablePrimaryActionButtonSx,
              boxShadow: '0 4px 12px rgba(255,170,0,0.2)',
            }}
          >
            Add New User
          </Button>
        )}
      </Box>
    ),
    muiTablePaperProps: {
      elevation: 0,
      sx: {
        borderRadius: 5,
        border: '1px solid',
        borderColor: 'divider',
        boxShadow: '0 10px 40px rgba(0,0,0,0.03)',
      },
    },
    muiTableHeadCellProps: {
      sx: {
        bgcolor: 'background.default',
        fontWeight: 800,
        color: 'text.secondary',
        fontSize: '13px',
        py: 2,
      },
    },
  });

  return fetchUsersError ? (
    <Error error={fetchUsersError?.response?.data?.error} />
  ) : (
    <Box sx={{ p: { xs: 2, md: 4 }, bgcolor: 'background.default', minHeight: '100%' }}>
      <MaterialReactTable table={table} />

      <Modal open={open} onClose={handleModalClose}>
        <Box>
          {view ? (
            <UsersDetail handleViewModalClose={handleModalClose} userId={selectedUserId} />
          ) : (
            <UsersUpdate handleModalClose={handleModalClose} refetch={refetch} />
          )}
        </Box>
      </Modal>

      {/* Status Update Confirmation Dialog */}
      <Dialog
        open={statusDialog.open}
        onClose={() => !isToggling && setStatusDialog({ open: false, user: null })}
        PaperProps={{ sx: { borderRadius: 6, p: 1, maxWidth: '400px' } }}
      >
        <DialogTitle sx={{ textAlign: 'center', pb: 0 }}>
          <Box sx={{
            width: 56,
            height: 56,
            borderRadius: '50%',
            bgcolor: statusDialog.user?.status === 'ACTIVE' ? 'error.lighter' : 'success.lighter',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mx: 'auto',
            mb: 2
          }}>
            <WarningAmber color={statusDialog.user?.status === 'ACTIVE' ? 'error' : 'success'} />
          </Box>
          <Typography variant="h6" sx={{ fontWeight: 800 }}>
            {statusDialog.user?.status === 'ACTIVE' ? "Deactivate User?" : "Activate User?"}
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ textAlign: 'center' }}>
          <Typography variant="body2" sx={{ color: 'text.secondary', mt: 1 }}>
            Are you sure you want to {statusDialog.user?.status === 'ACTIVE' ? "deactivate" : "activate"}{" "}
            <strong>{statusDialog.user?.first_name} {statusDialog.user?.last_name}</strong>?
            This will {statusDialog.user?.status === 'ACTIVE' ? "limit" : "restore"} their access to the platform.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 1, gap: 1 }}>
          <Button
            fullWidth
            variant="outlined"
            color="inherit"
            onClick={() => setStatusDialog({ open: false, user: null })}
            disabled={isToggling}
            sx={{ borderRadius: 3, textTransform: 'none', fontWeight: 600 }}
          >
            Cancel
          </Button>
          <Button
            fullWidth
            variant="contained"
            color={statusDialog.user?.status === 'ACTIVE' ? "error" : "success"}
            onClick={handleConfirmStatusToggle}
            disabled={isToggling}
            sx={{ borderRadius: 3, textTransform: 'none', fontWeight: 700 }}
          >
            {isToggling ? <CircularProgress size={24} color="inherit" /> : "Confirm"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default Users;
